import { getEgsMarginPercent } from '~/components/Offers/Detail/OfferBuilder/DealInformation/tabs/egs/egsUtils';

import type { OpportunityRelatedRecords } from '~/hooks/apis/offers/useGetOpportunityRelated';

import { uniqueBy } from '~/utils/arrayUtils';

import { SALESFORCE_FIELD_MAX_LENGTH } from '../../constants';
import { netToAud } from '../packageOptions/marginRules';
import { collectRoomAssets } from '../packageOptions/packageOptionsModel';
import type { OptionState, RoomAssetLink } from '../packageOptions/types';

import { EGS_CATS } from './fields';
import type {
  EgsApplyMode,
  EgsCategoryMeta,
  EgsDraft,
  EgsDraftInput,
  EgsGuestCategoryDraft,
  EgsGuestCategoryKey,
  EgsGuestType,
  EgsRoomDraft,
} from './types';

// Keyed by the same asset id as package option rooms, since room names are not unique on an Account.
// Only rooms set up in Package Options, since a surcharge on a room with no package never applies.
export function egsRooms(roomsByCategory: OptionState['roomsByCategory']): Array<RoomAssetLink> {
  return uniqueBy(
    roomsByCategory.flatMap((rooms) =>
      rooms
        .filter((room) => room.assetId && room.assetName)
        .map((room) => ({ id: room.assetId, name: room.assetName })),
    ),
    (room) => room.id,
  );
}

// For naming a saved record whose room has since left Package Options.
export function knownEgsRooms(
  opportunity: OpportunityRelatedRecords | undefined,
  roomsByCategory: OptionState['roomsByCategory'],
  roomAssets: Array<RoomAssetLink>,
  draftIdBySfid: Record<string, string> = {},
): Array<RoomAssetLink> {
  const opportunityRooms = collectRoomAssets(
    [...(opportunity?.deals ?? []), ...(opportunity?.rooms ?? [])].flatMap((record) =>
      record.room_asset ? [record.room_asset] : [],
    ),
    draftIdBySfid,
  );
  return uniqueBy([...egsRooms(roomsByCategory), ...opportunityRooms, ...roomAssets], (room) => room.id);
}

// Falls back to the stored value, so an id with no matching room still shows something.
export function egsRoomName(rooms: Array<RoomAssetLink>, roomId: string) {
  return rooms.find((room) => room.id === roomId)?.name ?? roomId;
}

export function getEgsRoomLabel(draft: Pick<EgsDraft, 'rooms'>, rooms: Array<RoomAssetLink>) {
  if (draft.rooms === 'all') {
    return 'All rooms';
  }
  return draft.rooms.map((roomId) => egsRoomName(rooms, roomId)).join(', ');
}

export function getEgsRecordName(draft: Pick<EgsDraft, 'rooms'>, rooms: Array<RoomAssetLink>) {
  const label = getEgsRoomLabel(draft, rooms);
  const maxLength = SALESFORCE_FIELD_MAX_LENGTH.egsRoomName;
  if (label.length <= maxLength || draft.rooms === 'all') {
    return label;
  }
  const roomNames = draft.rooms.map((roomId) => egsRoomName(rooms, roomId));
  for (let shownCount = roomNames.length - 1; shownCount > 0; shownCount -= 1) {
    const name = `${roomNames.slice(0, shownCount).join(', ')} +${roomNames.length - shownCount} more`;
    if (name.length <= maxLength) {
      return name;
    }
  }
  return `${label.slice(0, maxLength - 1)}…`;
}

export function getEgsBeddingLabel(draft: EgsDraft) {
  if (draft.beddingType === 'Other' && draft.otherBeddingType) {
    return draft.otherBeddingType;
  }
  return draft.beddingType;
}

export function defaultEgsCategoryDraft(): EgsGuestCategoryDraft {
  return {
    enabled: false,
    fromAge: '',
    toAge: '',
    inclusions: '',
    beddingType: '',
    otherBeddingType: '',
    rateType: '',
    costPrice: '',
    sellPriceAud: '',
    applyMode: 'this',
    applyRooms: [],
  };
}

export function guestCategoryKeyForType(guestType: EgsGuestType): EgsGuestCategoryKey {
  return EGS_CATS.find((meta) => meta.guestType === guestType)?.key ?? 'adult';
}

export function categoryDraftFromRecord(record: EgsDraft): EgsGuestCategoryDraft {
  return {
    enabled: true,
    fromAge: String(record.fromAge),
    toAge: String(record.toAge),
    inclusions: record.inclusions,
    beddingType: record.beddingType,
    otherBeddingType: record.otherBeddingType,
    rateType: record.rateType,
    costPrice: String(record.costPrice),
    sellPriceAud: String(record.sellPriceAud),
    applyMode: record.rooms === 'all' ? 'all' : record.rooms.length > 1 ? 'particular' : 'this',
    applyRooms: record.rooms === 'all' ? [] : record.rooms,
  };
}

export function defaultEgsRoomDraft(): EgsRoomDraft {
  return {
    globalApplyMode: 'this',
    globalApplyRooms: [],
    adult: defaultEgsCategoryDraft(),
    child: defaultEgsCategoryDraft(),
    infant: defaultEgsCategoryDraft(),
  };
}

export function buildEgsRecord(
  category: EgsGuestCategoryDraft,
  meta: EgsCategoryMeta,
  rooms: 'all' | Array<string>,
  currency: string,
  audToSourceCurrencyRate: Record<string, number | undefined>,
): EgsDraftInput {
  const fromAge = meta.ages === 'to' ? 0 : Number(category.fromAge);
  const toAge = meta.ages === 'from' ? 99 : Number(category.toAge);
  const costPrice = Number(category.costPrice);
  const sellPriceAud = Number(category.sellPriceAud);
  // Callers gate on a resolved, positive rate before reaching here (see applyRoomToPending),
  // so the costPrice fallback below is defensive only and should be unreachable.
  const convertedCost = netToAud(costPrice, currency, audToSourceCurrencyRate) ?? costPrice;

  return {
    rooms,
    guestType: meta.guestType,
    fromAge,
    toAge,
    collectedBy: costPrice === 0 && sellPriceAud === 0 ? 'Free of Charge' : 'Paid at Booking Stage',
    beddingType: category.beddingType,
    otherBeddingType: category.otherBeddingType,
    rateType: category.rateType,
    inclusions: category.inclusions.trim(),
    currency,
    costPrice,
    convertedCost,
    sellPriceAud,
    marginPct: getEgsMarginPercent(convertedCost, sellPriceAud),
  };
}

// Returns the same array when nothing changed so an unchanged rate doesn't dirty the session.
export function reconvertEgsDrafts(
  drafts: Array<EgsDraft>,
  currency: string,
  audToSourceCurrencyRate: Record<string, number | undefined>,
): Array<EgsDraft> {
  let changed = false;
  const nextDrafts = drafts.map((draft) => {
    const convertedCost = netToAud(draft.costPrice, currency, audToSourceCurrencyRate);
    if (convertedCost === undefined || (draft.currency === currency && draft.convertedCost === convertedCost)) {
      return draft;
    }
    changed = true;
    return {
      ...draft,
      currency,
      convertedCost,
      marginPct: getEgsMarginPercent(convertedCost, draft.sellPriceAud),
      updatedAt: new Date().toISOString(),
    };
  });
  return changed ? nextDrafts : drafts;
}

export const EGS_APPLY_MODE_LABELS: Record<EgsApplyMode, string> = {
  this: 'This room only',
  all: 'All rooms',
  particular: 'Select rooms',
};
